import { Button } from '@/components/ui/Button';
import { AlertTriangle, Banknote, ClipboardList, Clock3, Copy, ImageOff, Pencil, Search, ShieldAlert } from 'lucide-react';
import { useState } from 'react';

interface RejectReasonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  isLoading?: boolean;
  customerName: string;
}

const PREDEFINED_REASONS = [
  { id: 'illegible', label: 'Comprobante ilegible', icon: <ImageOff className="w-4 h-4 text-red-500" /> },
  { id: 'wrong_amount', label: 'Monto incorrecto', icon: <Banknote className="w-4 h-4 text-red-500" /> },
  { id: 'duplicate', label: 'Pago duplicado', icon: <Copy className="w-4 h-4 text-red-500" /> },
  { id: 'wrong_account', label: 'Cuenta incorrecta', icon: <ClipboardList className="w-4 h-4 text-red-500" /> },
  { id: 'expired', label: 'Comprobante vencido', icon: <Clock3 className="w-4 h-4 text-red-500" /> },
  { id: 'fake', label: 'Comprobante inválido', icon: <ShieldAlert className="w-4 h-4 text-red-500" /> },
  { id: 'no_reference', label: 'Sin referencia', icon: <Search className="w-4 h-4 text-red-500" /> },
  { id: 'other', label: 'Otro motivo', icon: <Pencil className="w-4 h-4 text-red-500" /> },
];

export function RejectReasonModal({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  customerName,
}: RejectReasonModalProps) {
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [customReason, setCustomReason] = useState('');

  if (!isOpen) return null;

  const handleConfirm = () => {
    let finalReason = '';

    if (selectedReason === 'other') {
      finalReason = customReason.trim() || 'Otro motivo no especificado';
    } else if (selectedReason) {
      const reason = PREDEFINED_REASONS.find((r) => r.id === selectedReason);
      finalReason = reason?.label || selectedReason;
    }

    onConfirm(finalReason);
  };

  const handleClose = () => {
    setSelectedReason(null);
    setCustomReason('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full transform transition-all"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-5 rounded-t-2xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Rechazar Pago</h3>
                <p className="text-red-100 text-sm">{customerName}</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <p className="text-sm text-gray-600 mb-4">
              Selecciona el motivo del rechazo. El cliente será notificado y los asientos serán
              liberados.
            </p>

            {/* Predefined reasons grid */}
            <div className="grid grid-cols-2 gap-2 mb-4">
              {PREDEFINED_REASONS.map((reason) => (
                <button
                  key={reason.id}
                  onClick={() => setSelectedReason(reason.id)}
                  className={`
                    p-3 rounded-xl border-2 text-left transition-all duration-150
                    ${
                      selectedReason === reason.id
                        ? 'border-red-500 bg-red-50 ring-2 ring-red-200'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }
                  `}
                >
                  <span className="text-lg mb-1 block">{reason.icon}</span>
                  <span
                    className={`text-sm font-medium ${
                      selectedReason === reason.id ? 'text-red-700' : 'text-gray-700'
                    }`}
                  >
                    {reason.label}
                  </span>
                </button>
              ))}
            </div>

            {/* Custom reason textarea */}
            {selectedReason === 'other' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Especifica el motivo:
                </label>
                <textarea
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  placeholder="Describe el motivo del rechazo..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                  rows={3}
                  autoFocus
                />
              </div>
            )}

            {/* Warning */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
              <div className="flex gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium mb-1">Esta acción:</p>
                  <ul className="list-disc list-inside space-y-0.5 text-amber-700">
                    <li>Cambiará el estado a "Rechazado"</li>
                    <li>Liberará los asientos reservados</li>
                    <li>No se puede deshacer</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 rounded-b-2xl flex gap-3 justify-end">
            <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={handleConfirm}
              disabled={!selectedReason || isLoading}
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Procesando...
                </span>
              ) : (
                <span className="inline-flex items-center gap-2">
                  <ShieldAlert className="w-4 h-4" />
                  Confirmar Rechazo
                </span>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
